import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { API_URL } from '../../config/api';
import CardScanner from '../CardScanner';

function PublicCardSubmission() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [tenant, setTenant] = useState(null);
  const [certTypes, setCertTypes] = useState([]);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    certification_type_id: '',
    certification_name: '',
    card_number: '',
    issue_date: '',
    expiry_date: '',
    issuer: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const fileInputRef = useRef(null);

  // Handle AI scan result - auto-fill form fields
  const handleScanComplete = (result) => {
    const { extractedData, imageBlob, croppedImageUrl } = result;

    // Auto-fill form fields from scanned data
    if (extractedData.card_number) {
      setFormData(prev => ({ ...prev, card_number: extractedData.card_number }));
    }

    if (extractedData.expiry_date) {
      setFormData(prev => ({ ...prev, expiry_date: extractedData.expiry_date }));
    }

    if (extractedData.issue_date) {
      setFormData(prev => ({ ...prev, issue_date: extractedData.issue_date }));
    }

    if (extractedData.issuer) {
      setFormData(prev => ({ ...prev, issuer: extractedData.issuer }));
    }

    // Try to match certification type
    if (extractedData.certification_type) {
      const matchedType = certTypes.find(type =>
        type.name.toLowerCase().includes(extractedData.certification_type.toLowerCase()) ||
        extractedData.certification_type.toLowerCase().includes(type.name.toLowerCase())
      );
      if (matchedType) {
        setFormData(prev => ({ ...prev, certification_type_id: matchedType.id }));
      } else {
        // If no match, set as custom name
        setFormData(prev => ({ ...prev, certification_name: extractedData.certification_type }));
      }
    }

    // Try to extract name from holder_name
    if (extractedData.holder_name && !formData.first_name && !formData.last_name) {
      const nameParts = extractedData.holder_name.trim().split(/\s+/);
      if (nameParts.length >= 2) {
        setFormData(prev => ({
          ...prev,
          first_name: nameParts[0],
          last_name: nameParts.slice(1).join(' ')
        }));
      }
    }

    // Set the scanned image
    setImageFile(new File([imageBlob], 'scanned_card.jpg', { type: 'image/jpeg' }));
    if (croppedImageUrl) {
      setImagePreview(croppedImageUrl);
    }

    // Show confidence warning
    if (extractedData.confidence === 'low') {
      setError('Kortin laatu heikko - tarkista tiedot huolellisesti');
    } else if (extractedData.confidence === 'medium') {
      setError('Tarkista skannatut tiedot ennen tallentamista');
    }

    setShowScanner(false);
  };

  // Validate token on load
  useEffect(() => {
    if (!token) {
      setError('Linkki ei ole kelvollinen. Pyydä uusi linkki ylläpitäjältä.');
      setLoading(false);
      return;
    }

    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await fetch(`${API_URL}/public/validate-token.php?token=${token}`);
      const data = await response.json();

      if (data.success) {
        setTokenValid(true);
        setTenant(data.tenant);
        setCertTypes(data.certification_types || []);
      } else {
        setError(data.message || 'Linkki ei ole voimassa');
      }
    } catch (err) {
      setError('Verkkovirhe. Yritä päivittää sivu.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setError('Sallitut tiedostotyypit: JPG, PNG, GIF, PDF');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('Tiedoston maksimikoko on 10 MB');
      return;
    }

    setImageFile(file);
    setError('');

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    // Validate required fields
    if (!formData.first_name || !formData.last_name) {
      setError('Etunimi ja sukunimi ovat pakollisia');
      setSubmitting(false);
      return;
    }

    if (!formData.email) {
      setError('Sähköpostiosoite on pakollinen');
      setSubmitting(false);
      return;
    }

    if (!formData.phone) {
      setError('Puhelinnumero on pakollinen');
      setSubmitting(false);
      return;
    }

    if (!formData.certification_type_id && !formData.certification_name) {
      setError('Valitse korttityyppi tai syötä kortin nimi');
      setSubmitting(false);
      return;
    }

    try {
      const submitData = new FormData();
      submitData.append('token', token);

      Object.entries(formData).forEach(([key, value]) => {
        if (value) submitData.append(key, value);
      });

      if (imageFile) {
        submitData.append('image', imageFile);
      }

      const response = await fetch(`${API_URL}/public/submit-card.php`, {
        method: 'POST',
        body: submitData
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
      } else {
        setError(data.message || 'Lähetys epäonnistui');
      }
    } catch (err) {
      setError('Verkkovirhe. Yritä uudelleen.');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.loading}>Ladataan...</div>
        </div>
      </div>
    );
  }

  // Error state (invalid token)
  if (!tokenValid) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.errorIcon}>⚠️</div>
          <h2 style={styles.title}>Linkki ei ole voimassa</h2>
          <p style={styles.description}>{error}</p>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.successIcon}>✓</div>
          <h2 style={styles.title}>Kortti lähetetty!</h2>
          <p style={styles.description}>
            Kiitos! Korttitietosi on vastaanotettu ja odottaa ylläpitäjän hyväksyntää.
          </p>
          <button
            onClick={() => {
              setSuccess(false);
              setFormData({
                first_name: formData.first_name,
                last_name: formData.last_name,
                email: formData.email,
                phone: formData.phone,
                certification_type_id: '',
                certification_name: '',
                card_number: '',
                issue_date: '',
                expiry_date: '',
                issuer: ''
              });
              setImageFile(null);
              setImagePreview(null);
            }}
            style={styles.button}
          >
            Lisää toinen kortti
          </button>
        </div>
      </div>
    );
  }

  // Group cert types by category
  const certTypesByCategory = certTypes.reduce((acc, type) => {
    const cat = type.category || 'Muut';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(type);
    return acc;
  }, {});

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Tenant Logo/Name */}
        {tenant?.logo_url ? (
          <img src={tenant.logo_url} alt={tenant.name} style={styles.logo} />
        ) : (
          <div style={styles.logoPlaceholder}>
            {tenant?.name?.charAt(0) || '?'}
          </div>
        )}

        <h2 style={styles.title}>Lähetä korttitiedot</h2>
        <p style={styles.description}>
          Täytä tietosi ja lisää kuva kortista. Ylläpitäjä tarkistaa ja hyväksyy kortin.
        </p>

        <form onSubmit={handleSubmit} style={styles.form}>
          {/* Personal Info Section */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Henkilötiedot</h3>

            <div style={styles.row}>
              <div style={styles.field}>
                <label style={styles.label}>Etunimi *</label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  style={styles.input}
                  required
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Sukunimi *</label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  style={styles.input}
                  required
                />
              </div>
            </div>

            <div style={styles.row}>
              <div style={styles.field}>
                <label style={styles.label}>Sähköposti *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  style={styles.input}
                  required
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Puhelin *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  style={styles.input}
                  required
                />
              </div>
            </div>
          </div>

          {/* Card Info Section */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Kortin tiedot</h3>

            {/* AI Scanner - at the top */}
            <div style={styles.scannerBox}>
              <button
                type="button"
                onClick={() => setShowScanner(true)}
                style={styles.scanButton}
              >
                <span style={{ fontSize: '20px' }}>📷</span>
                Skannaa kortti kameralla (AI)
              </button>
              <p style={styles.scannerInfo}>
                Ota kuva kortista, niin tekoäly tunnistaa ja täyttää tiedot automaattisesti.
                Tarkista tiedot ennen lähettämistä.
              </p>
            </div>

            <div style={styles.dividerLine} />

            <div style={styles.field}>
              <label style={styles.label}>Korttityyppi *</label>
              <select
                name="certification_type_id"
                value={formData.certification_type_id}
                onChange={handleInputChange}
                style={styles.input}
              >
                <option value="">Valitse korttityyppi...</option>
                {Object.entries(certTypesByCategory).map(([category, types]) => (
                  <optgroup key={category} label={category}>
                    {types.map(type => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Tai kortin nimi (jos ei listassa)</label>
              <input
                type="text"
                name="certification_name"
                value={formData.certification_name}
                onChange={handleInputChange}
                placeholder="Esim. Rakennusalan erikoiskortti"
                style={styles.input}
                disabled={!!formData.certification_type_id}
              />
            </div>

            <div style={styles.row}>
              <div style={styles.field}>
                <label style={styles.label}>Kortin numero</label>
                <input
                  type="text"
                  name="card_number"
                  value={formData.card_number}
                  onChange={handleInputChange}
                  style={styles.input}
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Myöntäjä</label>
                <input
                  type="text"
                  name="issuer"
                  value={formData.issuer}
                  onChange={handleInputChange}
                  placeholder="Esim. Rakennusteollisuus"
                  style={styles.input}
                />
              </div>
            </div>

            <div style={styles.row}>
              <div style={styles.field}>
                <label style={styles.label}>Myöntöpäivä</label>
                <input
                  type="date"
                  name="issue_date"
                  value={formData.issue_date}
                  onChange={handleInputChange}
                  style={styles.input}
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Voimassa asti</label>
                <input
                  type="date"
                  name="expiry_date"
                  value={formData.expiry_date}
                  onChange={handleInputChange}
                  style={styles.input}
                />
              </div>
            </div>
          </div>

          {/* Image Upload Section */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Kuva kortista</h3>

            <div
              onClick={() => fileInputRef.current?.click()}
              style={styles.uploadArea}
            >
              {imagePreview ? (
                <img src={imagePreview} alt="Esikatselu" style={styles.previewImage} />
              ) : imageFile ? (
                <div>
                  <div style={styles.fileIcon}>📄</div>
                  <div>{imageFile.name}</div>
                </div>
              ) : (
                <div>
                  <div style={styles.uploadIcon}>📷</div>
                  <div>Klikkaa lisätäksesi kuvan</div>
                  <div style={styles.uploadHint}>JPG, PNG, GIF tai PDF (max 10 MB)</div>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </div>

            {imageFile && (
              <button
                type="button"
                onClick={() => {
                  setImageFile(null);
                  setImagePreview(null);
                }}
                style={styles.removeButton}
              >
                Poista kuva
              </button>
            )}
          </div>

          {error && (
            <div style={styles.error}>{error}</div>
          )}

          <button
            type="submit"
            disabled={submitting}
            style={{
              ...styles.button,
              background: submitting ? '#64748b' : '#FFEB3B',
              cursor: submitting ? 'not-allowed' : 'pointer'
            }}
          >
            {submitting ? 'Lähetetään...' : 'Lähetä korttitiedot'}
          </button>
        </form>
      </div>

      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      {/* Card Scanner Modal */}
      {showScanner && (
        <CardScanner
          onClose={() => setShowScanner(false)}
          onScanComplete={handleScanComplete}
          publicToken={token}
        />
      )}
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    padding: '2rem 1rem'
  },
  card: {
    width: '100%',
    maxWidth: '560px',
    background: '#1e293b',
    borderRadius: '24px',
    padding: '2rem',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    animation: 'slideUp 0.5s ease-out'
  },
  logo: {
    width: '64px',
    height: '64px',
    objectFit: 'contain',
    margin: '0 auto 1rem',
    display: 'block'
  },
  logoPlaceholder: {
    width: '64px',
    height: '64px',
    background: '#FFEB3B',
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 1rem',
    fontSize: '28px',
    fontWeight: '700',
    color: '#0f172a'
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: '0.5rem',
    color: '#ffffff'
  },
  description: {
    textAlign: 'center',
    color: '#94a3b8',
    marginBottom: '1.5rem',
    fontSize: '14px'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem'
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0'
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#FFEB3B',
    marginBottom: '0.75rem'
  },
  row: {
    display: 'flex',
    gap: '1rem',
    flexWrap: 'wrap',
    marginBottom: '0'
  },
  field: {
    flex: 1,
    minWidth: '200px',
    marginBottom: '0.75rem'
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    fontSize: '14px',
    fontWeight: '500',
    color: '#ffffff'
  },
  input: {
    width: '100%',
    padding: '0.75rem 1rem',
    background: '#0f172a',
    border: '2px solid #334155',
    borderRadius: '12px',
    fontSize: '15px',
    outline: 'none',
    color: '#ffffff',
    boxSizing: 'border-box'
  },
  scannerBox: {
    background: 'rgba(99, 102, 241, 0.1)',
    border: '1px solid rgba(99, 102, 241, 0.3)',
    borderRadius: '12px',
    padding: '1rem',
    marginBottom: '0.75rem'
  },
  scanButton: {
    width: '100%',
    padding: '0.875rem',
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    transition: 'transform 0.2s, box-shadow 0.2s',
    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)'
  },
  scannerInfo: {
    margin: '0.75rem 0 0 0',
    fontSize: '13px',
    color: '#94a3b8',
    lineHeight: '1.4',
    textAlign: 'center'
  },
  dividerLine: {
    height: '1px',
    background: '#334155',
    margin: '0 0 0.75rem 0'
  },
  uploadArea: {
    padding: '2rem',
    border: '2px dashed #334155',
    borderRadius: '12px',
    textAlign: 'center',
    cursor: 'pointer',
    color: '#94a3b8',
    transition: 'border-color 0.2s'
  },
  uploadIcon: {
    fontSize: '48px',
    marginBottom: '0.5rem'
  },
  uploadHint: {
    fontSize: '12px',
    marginTop: '0.5rem',
    opacity: 0.7
  },
  previewImage: {
    maxWidth: '100%',
    maxHeight: '200px',
    objectFit: 'contain',
    borderRadius: '8px'
  },
  fileIcon: {
    fontSize: '48px',
    marginBottom: '0.5rem'
  },
  removeButton: {
    marginTop: '0.5rem',
    padding: '0.5rem 1rem',
    background: 'transparent',
    border: '1px solid #ef4444',
    borderRadius: '8px',
    color: '#ef4444',
    cursor: 'pointer',
    fontSize: '14px'
  },
  button: {
    width: '100%',
    padding: '1rem',
    background: '#FFEB3B',
    color: '#0f172a',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'transform 0.2s'
  },
  error: {
    padding: '0.875rem',
    background: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid #ef4444',
    borderRadius: '12px',
    color: '#ef4444',
    fontSize: '14px'
  },
  loading: {
    textAlign: 'center',
    color: '#94a3b8',
    padding: '2rem'
  },
  errorIcon: {
    fontSize: '64px',
    textAlign: 'center',
    marginBottom: '1rem'
  },
  successIcon: {
    width: '64px',
    height: '64px',
    background: '#22c55e',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 1rem',
    fontSize: '32px',
    color: '#ffffff'
  }
};

export default PublicCardSubmission;
